/**
 * Top-level route tree.
 *
 *   /                       public landing page
 *   /login                  public passwordless sign-in (email → OTP → session)
 *   /signup                 public passwordless signup (shares the login form)
 *   /subprocessors          public subprocessors list
 *   /docs/oauth             OAuth/MCP compliance docs (canonical URL shared with platform reviewers)
 *   /review/:platform       App Store / Play Store reviewer OTP display
 *   /dashboard/*            authenticated user dashboard
 *   /mfa-verify             post-login MFA placeholder (bounces to /dashboard)
 *
 * Marketing/legal/docs/FAQ pages live on oakallow.com (oakallow-nextjs).
 * Mobile apps and search engines are pointed there. Any unknown public path
 * here redirects to the home page.
 *
 * Every page is lazy-loaded so the initial bundle only contains the shell +
 * whatever page the user landed on. Combined with manualChunks in
 * vite.config.ts (react, recharts, lucide-react as separate vendor chunks),
 * this keeps the entry JS small and lets Cloudflare cache vendor chunks
 * independently from app code.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import RequireAuth from './components/RequireAuth';
import DashboardShell from './components/DashboardShell';

// Root path on oakallow.io hands off to the marketing site on oakallow.com.
// The two used to share a duplicate home page; consolidating means every
// /about, /pricing, /docs, etc. link works without maintaining two trees.
function MarketingRedirect() {
  if (typeof window !== 'undefined') {
    window.location.replace('https://oakallow.com/');
  }
  return null;
}
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const RecoveryPage = lazy(() => import('./pages/RecoveryPage'));
const OAuthAuthorizePage = lazy(() => import('./pages/OAuthAuthorizePage'));
const SubprocessorsPage = lazy(() => import('./pages/SubprocessorsPage'));
const OAuthDocsPage = lazy(() => import('./pages/OAuthDocsPage'));
const ReviewCodePage = lazy(() => import('./pages/review/ReviewCodePage'));
const SlackDecidePage = lazy(() => import('./pages/SlackDecidePage'));
const MFAVerifyPage = lazy(() => import('./pages/MFAVerifyPage'));
const PartnerApplyPage = lazy(() => import('./pages/PartnerApplyPage'));
const AddAuthorityPage = lazy(() => import('./pages/AddAuthorityPage'));
const PartnerVerifyDomainPage = lazy(() => import('./pages/PartnerVerifyDomainPage'));
const PartnerSelfPage = lazy(() => import('./pages/dashboard/PartnerSelfPage'));
const PartnerVerifyPage = lazy(() => import('./pages/PartnerVerifyPage'));
const PartnerAgreementPage = lazy(() => import('./pages/PartnerAgreementPage'));
const PartnerDashboardPage = lazy(() => import('./pages/PartnerDashboardPage'));
const PartnerMtlsSetupPage = lazy(() => import('./pages/PartnerMtlsSetupPage'));

const OverviewPage = lazy(() => import('./pages/dashboard/OverviewPage'));
const QuickstartPage = lazy(() => import('./pages/dashboard/QuickstartPage'));
const QuickstartMcpPage = lazy(() => import('./pages/dashboard/QuickstartMcpPage'));
const QuickstartOakGuardPage = lazy(() => import('./pages/dashboard/QuickstartOakGuardPage'));
const QuickstartAgentPage = lazy(() => import('./pages/dashboard/QuickstartAgentPage'));
const QuickstartRestPage = lazy(() => import('./pages/dashboard/QuickstartRestPage'));
const OrgsPage = lazy(() => import('./pages/dashboard/OrgsPage'));
const AgentSkillsPage = lazy(() => import('./pages/dashboard/AgentSkillsPage'));
const ConnectorGuidePage = lazy(() => import('./pages/dashboard/ConnectorGuidePage'));
const OrgIntegrationsPage = lazy(() => import('./pages/dashboard/OrgIntegrationsPage'));
const TenantsPage = lazy(() => import('./pages/dashboard/TenantsPage'));
const TenantDetailPage = lazy(() => import('./pages/dashboard/TenantDetailPage'));
const ResourcesPage = lazy(() => import('./pages/dashboard/ResourcesPage'));
const ResourceDetailPage = lazy(() => import('./pages/dashboard/ResourceDetailPage'));
const ToolsPage = lazy(() => import('./pages/dashboard/ToolsPage'));
const OakGuardPage = lazy(() => import('./pages/dashboard/OakGuardPage'));
const RulesPage = lazy(() => import('./pages/dashboard/RulesPage'));
const ApprovalsPage = lazy(() => import('./pages/dashboard/ApprovalsPage'));
const ActivityPage = lazy(() => import('./pages/dashboard/ActivityPage'));
const TeamPage = lazy(() => import('./pages/dashboard/TeamPage'));
const AgentsPage = lazy(() => import('./pages/dashboard/AgentsPage'));
const KeysPage = lazy(() => import('./pages/dashboard/KeysPage'));
const BillingPage = lazy(() => import('./pages/dashboard/BillingPage'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const RecipesPage = lazy(() => import('./pages/dashboard/RecipesPage'));
const RecipeEditPage = lazy(() => import('./pages/dashboard/RecipeEditPage'));
const SupportPage = lazy(() => import('./pages/dashboard/SupportPage'));
const SecurityPage = lazy(() => import('./pages/dashboard/SecurityPage'));
const SsoConfigPage = lazy(() => import('./pages/dashboard/sso/SsoConfigPage'));
const ScimConfigPage = lazy(() => import('./pages/dashboard/scim/ScimConfigPage'));
const BuilderPage = lazy(() => import('./pages/dashboard/BuilderPage'));
const TrackOverviewPage = lazy(() => import('./pages/dashboard/track/TrackOverviewPage'));
const TrackGovernancePage = lazy(() => import('./pages/dashboard/track/TrackGovernancePage'));
const TrackExplorePage = lazy(() => import('./pages/dashboard/track/TrackExplorePage'));
const TrackReportPage = lazy(() => import('./pages/dashboard/track/TrackReportPage'));
const TrackExecutionsPage = lazy(() => import('./pages/dashboard/track/TrackExecutionsPage'));
const TrackSettingsPage = lazy(() => import('./pages/dashboard/track/TrackSettingsPage'));

function PageLoading() {
  return (
    <div style={{ padding: '1.5rem', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<MarketingRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/recovery" element={<RecoveryPage />} />
          <Route path="/subprocessors" element={<SubprocessorsPage />} />
          <Route path="/docs/oauth" element={<OAuthDocsPage />} />
          <Route path="/review/:platform" element={<ReviewCodePage />} />
          {/* Slack step-up confirm page — public; the passkey ceremony is the
              auth. Linked from an ephemeral Slack Approve/Deny button. */}
          <Route path="/decide/:token" element={<SlackDecidePage />} />

          {/* Public partner-program info pages (mTLS setup is public for
              partner ops teams that need to reference it). */}
          <Route path="/partners/mtls-setup" element={<PartnerMtlsSetupPage />} />

          {/* Authenticated routes */}
          <Route element={<RequireAuth />}>
            <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
            <Route path="/mfa-verify" element={<MFAVerifyPage />} />

            {/* Verified Partner Program — application + verification.
                All require a signed-in oakallow account. The applicant
                becomes the partner's owner; their email must be on the
                corporate domain submitted. */}
            <Route path="/partners/apply" element={<PartnerApplyPage />} />
            <Route path="/partners/:id/verify-domain" element={<PartnerVerifyDomainPage />} />
            <Route path="/partners/:id/authorities/new" element={<AddAuthorityPage />} />
            <Route path="/partners/authorities/:id/verify" element={<PartnerVerifyPage />} />
            <Route path="/partners/authorities/:id/agreement" element={<PartnerAgreementPage />} />

            {/* Partner dashboard — authenticated but outside DashboardShell
                so partner staff get a focused view with no customer chrome. */}
            <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
            <Route element={<DashboardShell />}>
              <Route path="/dashboard" element={<OverviewPage />} />
              <Route path="/dashboard/quickstart" element={<QuickstartPage />} />
              <Route path="/dashboard/quickstart-mcp" element={<QuickstartMcpPage />} />
              <Route path="/dashboard/quickstart-oakguard" element={<QuickstartOakGuardPage />} />
              <Route path="/dashboard/quickstart-agent" element={<QuickstartAgentPage />} />
              <Route path="/dashboard/quickstart-rest" element={<QuickstartRestPage />} />
              <Route path="/dashboard/orgs" element={<OrgsPage />} />
              <Route path="/dashboard/skills" element={<AgentSkillsPage />} />
              <Route path="/dashboard/connector" element={<ConnectorGuidePage />} />
              <Route path="/dashboard/integrations" element={<OrgIntegrationsPage />} />
              <Route path="/dashboard/partner" element={<PartnerSelfPage />} />
              <Route path="/dashboard/tenants" element={<TenantsPage />} />
              <Route path="/dashboard/tenants/:externalId" element={<TenantDetailPage />} />
              <Route path="/dashboard/resources" element={<ResourcesPage />} />
              <Route path="/dashboard/resources/:externalId" element={<ResourceDetailPage />} />
              <Route path="/dashboard/tools" element={<ToolsPage />} />
              <Route path="/dashboard/oakguard" element={<OakGuardPage />} />
              <Route path="/dashboard/rules" element={<RulesPage />} />
              <Route path="/dashboard/approvals" element={<ApprovalsPage />} />
              <Route path="/dashboard/activity" element={<ActivityPage />} />
              <Route path="/dashboard/track" element={<TrackOverviewPage />} />
              <Route path="/dashboard/track/governance" element={<TrackGovernancePage />} />
              <Route path="/dashboard/track/explore" element={<TrackExplorePage />} />
              <Route path="/dashboard/track/report" element={<TrackReportPage />} />
              <Route path="/dashboard/track/executions" element={<TrackExecutionsPage />} />
              <Route path="/dashboard/track/settings" element={<TrackSettingsPage />} />
              <Route path="/dashboard/builder" element={<BuilderPage />} />
              <Route path="/dashboard/team" element={<TeamPage />} />
              <Route path="/dashboard/agents" element={<AgentsPage />} />
              <Route path="/dashboard/keys" element={<KeysPage />} />
              <Route path="/dashboard/billing" element={<BillingPage />} />
              <Route path="/dashboard/settings" element={<SettingsPage />} />
              <Route path="/dashboard/recipes" element={<RecipesPage />} />
              <Route path="/dashboard/recipes/:id" element={<RecipeEditPage />} />
              <Route path="/dashboard/support" element={<SupportPage />} />
              <Route path="/dashboard/security" element={<SecurityPage />} />
              <Route path="/dashboard/sso" element={<SsoConfigPage />} />
              <Route path="/dashboard/scim" element={<ScimConfigPage />} />
              <Route path="/dashboard/mfa" element={<Navigate to="/dashboard/security" replace />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
